import {useFormContext} from "react-hook-form";
import {
    ClientReportReportType,
    ClientReportReportTypeMap,
    ClientReportSubmitClientOfferHourInput,
    ClientReportSubmitClinicalRecommendationHourInput,
    ClientReportSubmitInput,
    ReportServiceType,
    requireServiceType
} from "../../domain";
import React, {useCallback, useMemo, useRef} from "react";
import {hourInputValidationRules, offeredHourRangeMap, shouldShowHourInputs, stopNonNumericInput} from "./view-domains";
import {useResetHiddenFieldsOnServiceTypeChange} from "./resetHiddenFields";
import {ServiceTypesMultiselectInput} from "./ServiceTypesMultiselectInput";
import {ServiceTypeSingleSelectInput} from "./ServiceTypesSingleSelectInput";
import {captureMessage, Severity} from "@sentry/react";
import {ServiceRecommendedInput} from "./ServiceRecommendedInput";
import {ServiceRecommended, ServiceRecommendedOptions} from "./domain";
import {TransitionRequestMismatchWarning} from "../../client-report/TransitionRequestMismatchWarning";
import {LatestProviderTransitionRequestCase} from "../../client-report/useLatestOfferedHoursForClientReport";
import {ProviderPortalFeatureFlag, useFeatureFlag} from "featureflag";

// BHPNPP-1858
function isAllowedNullMidlevelHours(serviceTypes: ReportServiceType[], field: keyof ClientReportSubmitClinicalRecommendationHourInput) {
    return field === 'practitionerMidLevelHoursMax'
        && serviceTypes.some((st) => st === ReportServiceType.Parent_Led_ABA || st === ReportServiceType.ABA_Parent_Training);
}

export function isEmptyValue(value: unknown): boolean {
    return value == null || (typeof value === 'string' && value.trim() === '');
}

/**
 * BHPNPP-3228: `disabledForHours` locks a field that was pre-populated by a business rule
 * (e.g. hours copied from the authorization). But a *required* locked field with a null/empty
 * value (0 counts as a real value, not empty) could never satisfy the "required" validation,
 * since the user would have no way to fill it in. So a required field is only locked if it
 * actually had a value at the moment it became locked. Optional fields don't have this problem
 * and are always locked as-is.
 *
 * The emptiness is captured once and remembered, rather than re-checking the live value on
 * every render, so that the user's own typing into a field that started out empty doesn't
 * immediately re-lock it.
 *
 * `disabled` (e.g. the form is submitting) is unrelated to this and always wins.
 */
export function useHourFieldDisabled(disabled: boolean, disabledForHours: boolean, isRequired: boolean, getCurrentValue: () => unknown): boolean {
    const wasLockedRef = useRef(false);
    const wasEmptyWhenLockedRef = useRef(true);

    if (disabledForHours && !wasLockedRef.current) {
        wasEmptyWhenLockedRef.current = isEmptyValue(getCurrentValue());
    }
    wasLockedRef.current = disabledForHours;

    const shouldBypassLock = isRequired && wasEmptyWhenLockedRef.current;
    return disabled || (disabledForHours && !shouldBypassLock);
}

type HourInputProps = {
    minField: keyof ClientReportSubmitClinicalRecommendationHourInput;
    maxField: keyof ClientReportSubmitClinicalRecommendationHourInput;
    label: string;
    // form is submitting / otherwise unavailable - always disable regardless of value
    disabled: boolean;
    // business rule locking pre-populated hours - only disable when there is an actual value to lock
    disabledForHours?: boolean;
};

export function HourInputRow({label, minField, maxField, disabled, disabledForHours = false}: HourInputProps) {
    const {
        formState: {
            errors
        },
        watch,
        getValues,
        register,
        trigger,
    } = useFormContext<ClientReportSubmitInput>();

    const dependentOfferedFields: Array<keyof ClientReportSubmitClientOfferHourInput> = useMemo(() => {
        const res = Array.from(offeredHourRangeMap.entries())
            .filter(([_, minMaxFields]) => minMaxFields.some(([min, max]) => min === minField || max === maxField));
        return res.map(([offerField, _]) => offerField);
    }, [minField, maxField]);

    const revalidateDependentOfferHours = useCallback(async () => {
        if (dependentOfferedFields != null && dependentOfferedFields.length > 0) {
            await Promise.all(dependentOfferedFields.map(
                (field) => trigger(field)
                    .catch((e) => {
                        // trigger() will throw exception on fields that are not on the form
                        // e.g. currentOffer*Hours field on report submission form
                        // we can safely ignore the error
                        if (!(e?.message ?? '').includes('Cannot read properties of undefined')) {
                            captureMessage(`Failed to trigger field '${field}' for offer hours by error`, {
                                level: Severity.Log,
                                contexts: e
                            });
                        }

                        return Promise.resolve();
                    })
            ));
        }
    }, [dependentOfferedFields, trigger]);

    const serviceTypes = watch('serviceType') as ReportServiceType[];

    // BHPNPP-1854: min is never required.
    const isMinRequired = false;
    const isMaxRequired = !isAllowedNullMidlevelHours(serviceTypes, maxField);

    const isMinDisabled = useHourFieldDisabled(disabled, disabledForHours, isMinRequired, () => getValues(minField));
    const isMaxDisabled = useHourFieldDisabled(disabled, disabledForHours, isMaxRequired, () => getValues(maxField));

    if (!shouldShowHourInputs(serviceTypes, [minField, maxField])) {
        return null;
    }

    const minFieldRegisteredInput = register(minField, {
        ...hourInputValidationRules,

        // BHPNPP-1854
        // this overrides the default required: true in hourValidationRules
        required: isMinRequired,

        validate: {
            ...hourInputValidationRules.validate,
        }
    });
    const defaultMinOnChange = minFieldRegisteredInput.onChange;
    minFieldRegisteredInput.onChange = async (event) => {
        if (await stopNonNumericInput(event)) {
            return;
        }
        await defaultMinOnChange(event);
        await trigger(minField);
        await revalidateDependentOfferHours();
    };

    const maxFieldRegisteredInput = register(maxField, {
        ...hourInputValidationRules,
        ...(isMaxRequired ? {} : { required: false }),
        validate: {
            ...hourInputValidationRules.validate,
            maxHourMinimum: (value: number) => {
                const min = getValues(minField);
                if (!isMaxRequired && min == null && value == null) {
                    return true;
                }
                if (min == null) {
                    return true;
                }
                return (value != null && value > min) || 'Max hour must be greater than min hour';
            },
        },
    });
    const defaultMaxOnChange = maxFieldRegisteredInput.onChange;
    maxFieldRegisteredInput.onChange = async (event) => {
        if (await stopNonNumericInput(event)) {
            return;
        }
        await defaultMaxOnChange(event);
        await trigger(maxField);
        await revalidateDependentOfferHours();
    };

    return (
        <div className="w-full flex">
            <div className="w-1/2 m-2">
                <div className="flex items-center">
                    <div className="w-2/3 text-right pr-3">
                        {label} - Min
                    </div>
                    <input
                        className={`block w-1/3 bg-white border border-gray-400 hover:border-atlantis-100 ` +
                            `p-2 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${isMinDisabled ? 'bg-gray-100 opacity-50 pointer-events-none' : ''}`}
                        autoComplete='off'
                        {...minFieldRegisteredInput}
                        disabled={isMinDisabled}
                    />
                </div>

                {
                    errors[minField] && (
                        <div className="text-red-700 text-right pt-1">
                            {errors[minField]?.message}
                        </div>
                    )
                }
            </div>

            <div className="w-1/2 m-2">
                <div className="flex items-center">
                    <div className="w-2/3 text-right pr-3">
                        {label} - Max
                    </div>
                    <input
                        className={`block w-1/3 bg-white border border-gray-400 hover:border-atlantis-100 ` +
                            `p-2 rounded shadow leading-tight focus:outline-none focus:shadow-outline ${isMaxDisabled ? 'bg-gray-100 opacity-50 pointer-events-none' : ''}`}
                        autoComplete='off'
                        {...maxFieldRegisteredInput}
                        disabled={isMaxDisabled}
                    />
                </div>

                {
                    errors[maxField] && (
                        <div className="text-red-700 text-right pt-1">
                            {errors[maxField]?.message}
                        </div>
                    )
                }
            </div>
        </div>
    );
}

export function shouldUseRestrictedOptions(reportType: string, restrictedOptions: Map<string, string> | null) {
    return ![
        ClientReportReportType.ABA_PROGRESS_REPORT,
        ClientReportReportType.ABA_ADDENDUM_REPORT,
        ClientReportReportType.CHAT_PROGRESS_REPORT,
        ClientReportReportType.CHAT_ADDENDUM_REPORT,
        ClientReportReportType.CONNECT_PROGRESS_REPORT,
        ClientReportReportType.CONNECT_ADDENDUM_REPORT,
        ClientReportReportType.CALIBRATE_PROGRESS_REPORT,
        ClientReportReportType.CALIBRATE_ADDENDUM_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_PROGRESS_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ADDENDUM_REPORT,
        ClientReportReportType.RUBI_PROGRESS_REPORT,
        ClientReportReportType.RUBI_ADDENDUM_REPORT,
        ClientReportReportType.ABA_TRANSFER_REEVALUATION_REPORT,
    ].some((type) => type === ClientReportReportTypeMap.get(reportType)) && restrictedOptions !== null;
}

export type ServiceLocationsFeat = {
    serviceLocationParaprofessionalInput: React.ReactNode;
    serviceLocationMidLevelInput: React.ReactNode;
    serviceLocationHighLevelInput: React.ReactNode;
    serviceLocationGroupCareInput: React.ReactNode;
}

type Props = {
    disabled: boolean;
    disabledForHours?: boolean;
    reportType: string;
    restrictedOptions?: Map<string, string> | null;
    isNewOfferForStaffingPlan?: boolean;
    isForStaffingPlan?: boolean;
    clinicalRecommendationChangingComponent?: React.ReactNode | null;
    serviceLocationsFeat?: ServiceLocationsFeat;
    transitionRequestData?: LatestProviderTransitionRequestCase | null;
    resetHiddenFieldsOnServiceTypeChange?: boolean;
};

const HOUR_RANGE_RESET_OPTIONS = {
    hourRanges: true,
};

export function ClinicalRecommendationInput({
                                                disabled,
                                                disabledForHours = false,
                                                reportType,
                                                isNewOfferForStaffingPlan = false,
                                                restrictedOptions = null,
                                                isForStaffingPlan = false,
                                                clinicalRecommendationChangingComponent = null,
                                                serviceLocationsFeat = undefined,
                                                transitionRequestData = null,
                                                resetHiddenFieldsOnServiceTypeChange = false,
                                            }: Props) {
    const {
        watch,
        setValue,
    } = useFormContext<ClientReportSubmitInput>();

    const isUpdatedTransferRequestDisabled = useFeatureFlag(ProviderPortalFeatureFlag.TDTL_9950_UPDATED_TRANSFER_REQUEST_DISABLED);

    const watchedServiceTypes = watch('serviceType') as ReportServiceType[];
    // `watch` returns a new array reference on every render, so memoize by
    // content to avoid re-triggering effects that depend on `serviceTypes`.
    const serviceTypesKey = JSON.stringify(watchedServiceTypes);
    const serviceTypes = useMemo(() => watchedServiceTypes ?? [], [serviceTypesKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const shouldRender = isNewOfferForStaffingPlan || requireServiceType(reportType);

    const resetValue = useCallback((field: string, value: null) => {
        setValue(field as any, value);
    }, [setValue]);

    useResetHiddenFieldsOnServiceTypeChange(
        serviceTypes,
        resetValue,
        HOUR_RANGE_RESET_OPTIONS,
        resetHiddenFieldsOnServiceTypeChange && shouldRender,
    );

    if (!shouldRender) {
        return null;
    }

    const showingCaregiverHours = shouldShowHourInputs(serviceTypes, [
        'caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax',
        'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',
    ]);

    const showingPractitionerHours = shouldShowHourInputs(serviceTypes, [
        'practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax',
        'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
        'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',
        'practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax',
    ]);

    const useRestrictedOptions = shouldUseRestrictedOptions(reportType, restrictedOptions);

    if (ServiceRecommendedOptions.get(watch("serviceRecommended")) === ServiceRecommended.No) {
        return (
            <div>
                <div className="w-full text-base pb-2">
                    <div className="font-bold pb-2">
                        Clinical Recommendations
                    </div>

                    <ServiceRecommendedInput reportType={reportType} isForStaffingPlan={isForStaffingPlan}
                                             disabled={disabled}/>

                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="w-full text-base pb-2">
                <div className="font-bold pb-2">
                    Clinical Recommendations
                </div>

                {clinicalRecommendationChangingComponent}

                <ServiceRecommendedInput reportType={reportType} isForStaffingPlan={isForStaffingPlan}
                                         disabled={disabled}/>

                {useRestrictedOptions &&
                    <ServiceTypeSingleSelectInput restrictedOptions={restrictedOptions!} disabled={disabled || disabledForHours}/>
                }
                {!useRestrictedOptions &&
                    <ServiceTypesMultiselectInput disabled={disabled || disabledForHours}/>
                }

                {showingCaregiverHours && (
                    <div className="pb-2 text-black-200 items-center text-base">
                        <div className="italic">
                            Caregiver Mediated Treatment (hours per month)
                        </div>

                        <HourInputRow
                            label="Mid-level Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'caregiverMidLevelHoursMin'}
                            maxField={'caregiverMidLevelHoursMax'}
                        />

                        <HourInputRow
                            label="High-level Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'caregiverHighLevelHoursMin'}
                            maxField={'caregiverHighLevelHoursMax'}
                        />
                    </div>
                )}

                {showingPractitionerHours && (
                    <div className="pb-2 text-black-200 items-center text-base">
                        <div className="italic">
                            Practitioner Mediated Treatment (hours per month)
                        </div>

                        <HourInputRow
                            label="Paraprofessional Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'practitionerParaprofessionalHoursMin'}
                            maxField={'practitionerParaprofessionalHoursMax'}
                        />

                        <HourInputRow
                            label="Mid-level Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'practitionerMidLevelHoursMin'}
                            maxField={'practitionerMidLevelHoursMax'}
                        />

                        <HourInputRow
                            label="High-level Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'practitionerHighLevelHoursMin'}
                            maxField={'practitionerHighLevelHoursMax'}
                        />

                        <HourInputRow
                            label="Group Care Hours"
                            disabled={disabled}
                            disabledForHours={disabledForHours}
                            minField={'practitionerGroupCareHoursMin'}
                            maxField={'practitionerGroupCareHoursMax'}
                        />
                    </div>
                )}
                {
                    serviceLocationsFeat && (
                        <div className="pb-2">
                            { showingPractitionerHours && shouldShowHourInputs(serviceTypes, ['practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax']) 
                                && serviceLocationsFeat?.serviceLocationParaprofessionalInput}
                            { (shouldShowHourInputs(serviceTypes, ['practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax'])
                                || shouldShowHourInputs(serviceTypes, ['caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax']))
                                && serviceLocationsFeat?.serviceLocationMidLevelInput}
                            { (shouldShowHourInputs(serviceTypes, ['practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax'])
                                || shouldShowHourInputs(serviceTypes, ['caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax']))
                                && serviceLocationsFeat?.serviceLocationHighLevelInput}
                            { showingPractitionerHours && shouldShowHourInputs(serviceTypes, ['practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax']) 
                                && serviceLocationsFeat?.serviceLocationGroupCareInput}
                        </div>
                    )
                }

                {!isUpdatedTransferRequestDisabled && transitionRequestData && (
                    <TransitionRequestMismatchWarning transitionRequestData={transitionRequestData} />
                )}
            </div>
        </div>
    );
}
